"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.framerWebPublisher = exports.FRAMER_WEB_PUBLISHER_BG = exports.FRAMER_WEB_PUBLISHER_COLOR = exports.FRAMER_WEB_PUBLISHER_CATEGORY = exports.framerWebPublisherArgsSchema = exports.FramerWebPublisherAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var webPublisher_1 = require("./webPublisher");
// ==================== Core WebPublisher Actions ====================
// Create args with workflow node reference (SEO Article Writer)
var framerWebPublisherCreateWithNodeArgsSchema = zod_1.z.object({
    seoArticleWriterNodeId: zod_1.z
        .string()
        .describe('ID of the SEO Article Writer workflow node containing the content to publish. CRITICAL: This must be a node execution ID from seoArticleWriter tool only, never from seoArticleUpdater or other tools.'),
    collectionName: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Name of the Framer CMS collection to create the item in (if not specified, will be inferred from content)'),
    draft: zod_1.z.boolean().optional().nullable().describe('Whether to create as draft (default: false)'),
});
// Create args with direct content
var framerWebPublisherCreateWithContentArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Framer CMS collection to create the item in'),
    content: webPublisher_1.webPublishContentDataSchema.extend({
        fieldData: zod_1.z
            .record(zod_1.z.any())
            .optional()
            .nullable()
            .describe('Additional field data for the Framer CMS item'),
    }),
    draft: zod_1.z.boolean().optional().nullable().describe('Whether to create as draft (default: false)'),
});
// Update args with workflow node reference (SEO Article Updater)
var framerWebPublisherUpdateWithNodeArgsSchema = zod_1.z.object({
    seoArticleUpdaterNodeId: zod_1.z
        .string()
        .describe('ID of the SEO Article Updater workflow node containing the changes to apply. CRITICAL: This must be a node execution ID from seoArticleUpdater tool only, never from seoArticleWriter or other tools.'),
    collectionName: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Name of the Framer CMS collection (if not specified, will be inferred from updater result)'),
});
// Update args with direct changes
var framerWebPublisherUpdateWithChangesArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Framer CMS collection'),
    itemId: zod_1.z.string().describe('ID of the Framer CMS item to update'),
    changes: zod_1.z
        .array(common_1.contentChangeSchema)
        .optional()
        .nullable()
        .describe('List of changes to apply to the content'),
    fieldUpdates: zod_1.z
        .record(zod_1.z.any())
        .optional()
        .nullable()
        .describe('Direct field updates for the Framer CMS item'),
    draft: zod_1.z.boolean().optional().nullable().describe('Whether to set as draft'),
});
// Subagent task runner args
var framerWebPublisherSubagentArgsSchema = zod_1.z
    .string()
    .describe('Natural language instructions for miscellaneous Framer tasks with direct API access. The subagent will attempt to execute the task using Framer APIs. Include clear instructions about what you want to accomplish (e.g., "Update hero section text", "Create new blog post CMS item", "Change image in gallery section").');
// ==================== Extended Framer Actions ====================
// Get content args
var framerGetContentArgsSchema = zod_1.z.object({
    collectionName: zod_1.z.string().describe('Name of the Framer CMS collection'),
    slug: zod_1.z.string().describe('Slug of the item to retrieve'),
});
// List collections args
var framerListCollectionsArgsSchema = zod_1.z.object({});
// Get publish URLs args
var framerGetPublishUrlsArgsSchema = zod_1.z.object({});
// ==================== Discriminated Union for All Actions ====================
exports.FramerWebPublisherAction = zod_1.z.enum([
    // Core WebPublisher actions
    'create',
    'update',
    'subagentTaskRunner',
    // Extended Framer actions
    'getContent',
    'listCollections',
    'getPublishUrls',
]);
exports.framerWebPublisherArgsSchema = zod_1.z.object({
    action: exports.FramerWebPublisherAction,
    args: zod_1.z.union([
        // Core create actions
        framerWebPublisherCreateWithNodeArgsSchema,
        framerWebPublisherCreateWithContentArgsSchema,
        // Core update actions
        framerWebPublisherUpdateWithNodeArgsSchema,
        framerWebPublisherUpdateWithChangesArgsSchema,
        // Core subagent action
        framerWebPublisherSubagentArgsSchema,
        // Extended actions
        framerGetContentArgsSchema,
        framerListCollectionsArgsSchema,
        framerGetPublishUrlsArgsSchema,
    ]),
});
// Dynamic result schemas - all return the same WebPublisherResult format
var framerWebPublisherResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action'],
    variants: [
        { when: { action: 'create' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'update' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'subagentTaskRunner' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'getContent' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listCollections' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'getPublishUrls' }, schema: webPublisher_1.webPublisherResultSchema },
    ],
    defaultSchema: webPublisher_1.webPublisherResultSchema,
});
// ==================== Tool Metadata ====================
exports.FRAMER_WEB_PUBLISHER_CATEGORY = common_1.ToolCategorySchema.enum.integration;
exports.FRAMER_WEB_PUBLISHER_COLOR = common_1.BRAND_COLORS.FRAMER;
exports.FRAMER_WEB_PUBLISHER_BG = (0, common_1.createBgColor)(exports.FRAMER_WEB_PUBLISHER_COLOR);
exports.framerWebPublisher = (0, common_1.createToolMetadata)({
    id: 'framerWebPublisher',
    displayName: 'Framer Web Publisher',
    displayDescription: 'Unified Framer web publisher. Core: create/update CMS items with SEO integration, subagentTaskRunner for natural language tasks. Extended: full Framer API operations.',
    category: exports.FRAMER_WEB_PUBLISHER_CATEGORY,
    color: exports.FRAMER_WEB_PUBLISHER_COLOR,
    backgroundColor: exports.FRAMER_WEB_PUBLISHER_BG,
    icon: 'framer',
    argsSchema: exports.framerWebPublisherArgsSchema,
}, framerWebPublisherResultSchemas);
